import React, { ChangeEvent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { setGlobalError } from '../../captains-form/components/errors/slices/globalErrorSlice';
import {
  selectIsLoading,
  State,
} from '../../captains-form/slices/documentsSlice';

import Button from '../../layout/components/Button';
import Modal from '../../layout/components/Modal';

import cloudUpload from '../styles/images/CloudUpload.svg';
import cross from '../styles/images/Cross-X.svg';
import eye from '../styles/images/Eye.svg';
import iconPicture from '../styles/images/icon-picture.svg';
import uploadBtn from '../styles/images/upload-btn.svg';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import '../styles/drag-and-drop-picture.scss';

interface DragAndDropPictureProps {
  title: string;
  openModalButtonText?: string;
  uploadButtonText?: string;
  acceptedFormats: string[];
  vesselStamp?: string | null;
  setVesselStamp: (value: string | null) => void;
}

function DragAndDropPicture({
  title,
  openModalButtonText = 'Upload image',
  uploadButtonText = 'Upload',
  acceptedFormats,
  vesselStamp,
  setVesselStamp,
}: DragAndDropPictureProps) {
  const [temporaryFiles, setTemporaryFiles] = useState<File[] | null>(null);
  const [temporaryVesselStamp, setTemporaryVesselStamp] = useState<
    string | null
  >(vesselStamp!);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isLoading = useSelector((state: State) =>
    selectIsLoading(state, 'vesselStamp')
  );
  const dispatch = useDispatch();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    const acceptedFiles = checkFileExtension(files);
    if (acceptedFiles && acceptedFiles.length > 0) {
      setTemporaryFiles(files ? Array.from(files) : null);
    } else {
      setIsModalOpen(false);
      dispatch(setGlobalError('Uploaded image should be a jpeg/jpg/png'));
    }

    if (!isFileSizeAccepted(files ? files[0].size : null)) {
      setIsModalOpen(false);
      setTemporaryFiles(null);
      dispatch(setGlobalError('Uploaded image should be up to 1MB'));
    }
  };

  const checkFileExtension = (files: FileList | null) => {
    if (files) {
      const acceptedFiles = Array.from(files).filter((file) => {
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        return fileExt && acceptedFormats.includes(`.${fileExt}`);
      });
      return acceptedFiles;
    }
  };

  const isFileSizeAccepted = (size: number | null) => {
    const MAX_FILE_SIZE = 1 * 1024 * 1024;
    if (size) {
      if (size < MAX_FILE_SIZE) {
        return true;
      }
      return false;
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    const acceptedFiles = checkFileExtension(files);
    if (acceptedFiles && acceptedFiles.length > 0) {
      setTemporaryFiles(acceptedFiles);
    } else {
      setIsModalOpen(false);
      dispatch(setGlobalError('Uploaded image should be a jpeg/jpg/png'));
    }

    if (!isFileSizeAccepted(event.dataTransfer.files[0].size)) {
      setIsModalOpen(false);
      setTemporaryFiles(null);
      dispatch(setGlobalError('Uploaded image should be up to 1MB'));
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleUpload = async () => {
    if (temporaryFiles) {
      const file = temporaryFiles[0];
      const base64 = await getBase64(file);
      try {
        setTemporaryVesselStamp(base64);
        setVesselStamp(base64);
        handleCloseModal(true);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file); // reads file as base64-encoded data URL
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // This result will look like: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgA..."
          const base64String = reader.result;
          resolve(base64String);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const downloadBase64Image = (base64: string, filename: string) => {
    const link = document.createElement('a');
    link.href = base64;
    link.download = filename; // e.g., "image.png"
    document.body.appendChild(link); // Required for Firefox
    link.click();
    document.body.removeChild(link); // Clean up
  };

  const handleRemoveFile = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    const fileIndex = Number(event.currentTarget.id);
    const newFiles =
      temporaryFiles && temporaryFiles.length > 1
        ? temporaryFiles.filter((file, index) => index !== fileIndex)
        : null;
    setTemporaryFiles(newFiles);
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setVesselStamp(null);
    setTemporaryVesselStamp(null);
  };

  const handleCloseModal = (cleanFilelist = false) => {
    setIsModalOpen(false);
    cleanFilelist && setTemporaryFiles(null);
  };

  function handleDownload() {
    // @ts-ignore
    downloadBase64Image(temporaryVesselStamp, 'vessel_stamp');
  }

  const renderVesselStamp = () => (
    <div className="drag-and-drop__uploadedList">
      <div className="drag-and-drop_file_card">
        <div className="drag-and-drop_file_card_content">
          <div className="drag-and-drop_file_card_content_title">
            <img
              className="drag-and-drop_file_card_content_title_icon"
              src={iconPicture}
              alt="image-icon"
            />
            <div className="drag-and-drop_file_card_content_title_text">
              <span className="drag-and-drop_file_card_content_title_text__title">
                {'Vessel Stamp'}
              </span>
            </div>
          </div>
          <div className="drag-and-drop_file_card_content_btns">
            <Button
              className="drag-and-drop_file_card_content_btns__open light"
              iconMiddle={eye}
              type="button"
              onClick={() => handleDownload()}>
              Open
            </Button>
            <Button
              className="drag-and-drop_file_card_content_btns__cross"
              iconMiddle={cross}
              onClick={() => setIsConfirmationModalOpen(true)}></Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {temporaryVesselStamp ? (
        <div className="drag-and-drop">
          <div className={'info'}>
            <div className="drag-and-drop__title">
              <span>{title}</span>
            </div>
            {renderVesselStamp()}
          </div>
        </div>
      ) : (
        <Button
          iconLeft={uploadBtn}
          className="image-importer-button-left light-table"
          onClick={() => {
            setIsModalOpen(!isModalOpen);
          }}>
          {openModalButtonText}
        </Button>
      )}
      {isModalOpen ? (
        <Modal
          title="Upload a picture"
          showModal={isModalOpen}
          onCloseModal={handleCloseModal}
          //@ts-ignore
          hideCloseBtn={true}
          icon={cloudUpload}
          subtitle={'Upload the vessel stamp.'}>
          {isLoading ? (
            <div className={'drag-and-drop_modal_loading'}>
              <FontAwesomeIcon
                spin
                icon={faSpinner}
                className="loader"
                size="3x"
                style={{ color: '#5b8fe9' }}
              />
            </div>
          ) : (
            <>
              {temporaryFiles && temporaryFiles.length > 0 ? (
                <div className="drag-and-drop_modal_filelist">
                  <span className="drag-and-drop_modal_filelist__label">
                    Attached files
                  </span>
                  <ul className="drag-and-drop_modal_filelist__list">
                    {Array.from(temporaryFiles).map((file, index) => (
                      <li key={index}>
                        <span>{file.name}</span>
                        <Button
                          iconMiddle={cross}
                          onClick={handleRemoveFile}></Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div
                  className="drag-and-drop_modal_dropzone"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}>
                  <input
                    type="file"
                    accept={acceptedFormats
                      .map((format) => format.trim())
                      .join(',')}
                    onChange={handleFileChange}
                    multiple={false}
                    hidden
                    ref={inputRef}
                  />
                  <span>Drag the file here</span>
                  <span>or</span>
                  <Button
                    className="light"
                    onClick={() => inputRef?.current?.click()}>
                    Select File
                  </Button>
                </div>
              )}
            </>
          )}

          <div className="drag-and-drop_modal_btns">
            <Button
              id="backButton"
              className="transparent"
              onClick={handleCloseModal}>
              Back
            </Button>
            {temporaryFiles && temporaryFiles.length > 0 ? (
              <Button id="uploadButton" onClick={handleUpload}>
                {uploadButtonText}
              </Button>
            ) : null}
          </div>
        </Modal>
      ) : null}
      {isConfirmationModalOpen ? (
        <Modal
          title="Are you sure you want to delete the vessel stamp?"
          showModal={isConfirmationModalOpen}
          onCloseModal={handleCloseModal}
          //@ts-ignore
          hideCloseBtn={true}>
          <div className="drag-and-drop_modal_btns">
            <Button
              id="backButton"
              className="light"
              onClick={() => setIsConfirmationModalOpen(false)}>
              Back
            </Button>
            <Button
              id="backButton"
              className="danger"
              onClick={() => handleCloseConfirmationModal()}>
              Delete
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

export default DragAndDropPicture;
